import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { t } from "../../i18n";
import { ShareWidgetModal } from "../../components/ShareWidgetModal";
import { CheckWidgetModal } from "../../components/CheckWidgetModal";
import AgentForm from "./components/AgentForm";

const API_URL = process.env.REACT_APP_API_URL || "https://b.supchats.com";
const WS_URL = process.env.REACT_APP_WS_URL || "wss://b.supchats.com";

/** Преобразует относительный URL иконки в полный (статику отдаёт бэкенд, не фронт) */
function toAbsoluteIconUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = API_URL.replace(/\/$/, "");
  return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
}

/** Нормализует список URL иконок агента (массив для отображения в форме редактирования) */
function getAgentIconUrls(agent) {
  if (!agent) return [];
  const urls = agent.icon_urls;
  if (Array.isArray(urls) && urls.length > 0) return urls;
  const single = agent.widget_icon_url;
  return single ? [single] : [];
}

function TruncatedText({ text, maxLength = 20 }) {
  if (!text) return null;
  if (text.length <= maxLength) return <>{text}</>;

  return (
    <span className="truncated-text-container">
      <span className="truncated-text">{text.substring(0, maxLength)}...</span>
      <span className="truncated-tooltip">{text}</span>
    </span>
  );
}

export default function AgentsPage({ token, setToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [agents, setAgents] = useState([]);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activeSelection, setActiveSelection] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [contactDetails, setContactDetails] = useState("");
  const [knowledgeBase, setKnowledgeBase] = useState("");
  const [trainingLinks, setTrainingLinks] = useState("");
  const [faq, setFaq] = useState("");
  const [domain, setDomain] = useState("");
  const [widgetColor, setWidgetColor] = useState("#667eea");
  const [useSupportStyle, setUseSupportStyle] = useState(false);
  const [supportName, setSupportName] = useState("Support");
  const [autoOpenEnabled, setAutoOpenEnabled] = useState(false);
  const [autoOpenDelay, setAutoOpenDelay] = useState(0);
  const [autoArchiveEnabled, setAutoArchiveEnabled] = useState(false);
  const [autoArchiveDelay, setAutoArchiveDelay] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [generatedScript, setGeneratedScript] = useState("");
  const [scriptAgentId, setScriptAgentId] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [checkWidgetAgent, setCheckWidgetAgent] = useState(null);
  const [isGeneratingFaq, setIsGeneratingFaq] = useState(false);
  const [isGeneratingLinks, setIsGeneratingLinks] = useState(false);
  const [message, setMessage] = useState("");

  const [scriptGenerated, setScriptGenerated] = useState(() => {
    try {
      const saved = localStorage.getItem("generated_scripts");
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  }); // { [agentId]: script }
  const [editingAgent, setEditingAgent] = useState(null);
  const [userLimits, setUserLimits] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showAgentSelectionModal, setShowAgentSelectionModal] = useState(false);
  const [agentToActivate, setAgentToActivate] = useState(null);
  const agentFormRef = useRef(null);
  // Подсветка кнопки «Добавить агента» после завершения курса новичка (5 мин)
  const [showAddAgentHighlight, setShowAddAgentHighlight] = useState(() => {
    try {
      const ts = localStorage.getItem("welcome_tour_highlight_add_agent");
      if (!ts) return false;
      const age = Date.now() - parseInt(ts, 10);
      return age < 5 * 60 * 1000;
    } catch (e) {
      return false;
    }
  });

  const [createIconFile, setCreateIconFile] = useState(null);
  const [createIconPreviewUrl, setCreateIconPreviewUrl] = useState("");
  const createIconPreviewUrlRef = useRef(null); // для отмены object URL
  const [isUploadingCreateIcon, setIsUploadingCreateIcon] = useState(false);
  const [createIconUploadError, setCreateIconUploadError] = useState("");

  const [isUploadingEditIcon, setIsUploadingEditIcon] = useState(false);
  const [editIconUploadError, setEditIconUploadError] = useState("");
  const [editIconPreviewUrl, setEditIconPreviewUrl] = useState("");
  const editIconPreviewUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (createIconPreviewUrlRef.current) {
        URL.revokeObjectURL(createIconPreviewUrlRef.current);
        createIconPreviewUrlRef.current = null;
      }
    };
  }, []);

  // Подсветка после перехода с курса новичка (когда пользователь уже на /agents)
  useEffect(() => {
    if (location.state?.fromTour) {
      setShowAddAgentHighlight(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.fromTour, navigate, location.pathname]);

  const setCreateIconPreview = (file) => {
    if (createIconPreviewUrlRef.current) {
      URL.revokeObjectURL(createIconPreviewUrlRef.current);
      createIconPreviewUrlRef.current = null;
    }
    if (!file) {
      setCreateIconPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(file);
    createIconPreviewUrlRef.current = url;
    setCreateIconPreviewUrl(url);
  };

  const createFormValue = {
    name: agentName,
    prompt,
    welcome_message: welcomeMessage,
    contact_details: contactDetails,
    knowledge_base: knowledgeBase,
    training_links: trainingLinks,
    faq,
    domain,
    widget_color: widgetColor,
    use_support_style: useSupportStyle,
    support_name: supportName,
    auto_open_enabled: autoOpenEnabled,
    auto_open_delay: autoOpenDelay,
    auto_archive_enabled: autoArchiveEnabled,
    auto_archive_delay: autoArchiveDelay,
    sound_enabled: soundEnabled,
    widget_icon_url: null,
  };

  const patchCreateForm = (patch) => {
    if (Object.prototype.hasOwnProperty.call(patch, "name")) {
      setAgentNameWithSave(patch.name);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "prompt")) {
      setPromptWithSave(patch.prompt);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "welcome_message")) {
      setWelcomeMessageWithSave(patch.welcome_message);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "contact_details")) {
      setContactDetailsWithSave(patch.contact_details);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "knowledge_base")) {
      setKnowledgeBaseWithSave(patch.knowledge_base);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "training_links")) {
      setTrainingLinksWithSave(patch.training_links);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "faq")) {
      setFaqWithSave(patch.faq);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "domain")) {
      setDomainWithSave(patch.domain);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "widget_color")) {
      setWidgetColorWithSave(patch.widget_color);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "use_support_style")) {
      setUseSupportStyleWithSave(patch.use_support_style);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "support_name")) {
      setSupportNameWithSave(patch.support_name);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "auto_open_enabled")) {
      setAutoOpenEnabledWithSave(patch.auto_open_enabled);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "auto_open_delay")) {
      setAutoOpenDelayWithSave(patch.auto_open_delay);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "auto_archive_enabled")) {
      setAutoArchiveEnabledWithSave(patch.auto_archive_enabled);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "auto_archive_delay")) {
      setAutoArchiveDelayWithSave(patch.auto_archive_delay);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "sound_enabled")) {
      setSoundEnabled(patch.sound_enabled);
      setTimeout(saveFormData, 100);
    }
  };

  const patchEditingAgent = (patch) => {
    setEditingAgent((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const saveFormData = () => {
    const formData = {
      agentName,
      prompt,
      welcomeMessage,
      contactDetails,
      knowledgeBase,
      trainingLinks,
      faq,
      domain,
      widgetColor,
      useSupportStyle,
      supportName,
      autoOpenEnabled,
      autoOpenDelay,
      autoArchiveEnabled,
      autoArchiveDelay,
      soundEnabled,
      timestamp: Date.now(),
    };
    try {
      localStorage.setItem("agent_form_data", JSON.stringify(formData));
    } catch (e) {
      console.error("Failed to save form data:", e);
    }
  };

  const saveFormState = () => {
    try {
      localStorage.setItem(
        "agent_form_open",
        JSON.stringify({
          isOpen: showForm,
          timestamp: Date.now(),
        })
      );
    } catch (e) {
      console.error("Failed to save form state:", e);
    }
  };

  const loadFormData = () => {
    try {
      const saved = localStorage.getItem("agent_form_data");
      if (saved) {
        const formData = JSON.parse(saved);
        const oneWeek = 7 * 24 * 60 * 60 * 1000;
        const isExpired =
          formData.timestamp && Date.now() - formData.timestamp > oneWeek;
        if (isExpired) {
          localStorage.removeItem("agent_form_data");
          return;
        }
        setAgentName(formData.agentName || "");
        setPrompt(formData.prompt || "");
        setWelcomeMessage(formData.welcomeMessage || "");
        setContactDetails(formData.contactDetails || "");
        setKnowledgeBase(formData.knowledgeBase || "");
        setTrainingLinks(formData.trainingLinks || "");
        setFaq(formData.faq || "");
        setDomain(formData.domain || "");
        setWidgetColor(formData.widgetColor || "#667eea");
        setUseSupportStyle(formData.useSupportStyle || false);
        setSupportName(formData.supportName || "Support");
        setAutoOpenEnabled(formData.autoOpenEnabled || false);
        setAutoOpenDelay(formData.autoOpenDelay || 0);
        setAutoArchiveEnabled(formData.autoArchiveEnabled || false);
        setAutoArchiveDelay(formData.autoArchiveDelay || 0);
        setSoundEnabled(
          typeof formData.soundEnabled === "boolean" ? formData.soundEnabled : true
        );
      }
    } catch (e) {
      console.error("Failed to load form data:", e);
    }
  };

  const loadFormState = () => {
    try {
      const saved = localStorage.getItem("agent_form_open");
      if (saved) {
        const formState = JSON.parse(saved);

        const oneWeek = 7 * 24 * 60 * 60 * 1000;
        const isExpired =
          formState.timestamp && Date.now() - formState.timestamp > oneWeek;

        if (isExpired) {
          localStorage.removeItem("agent_form_open");
          return;
        }

        if (formState.isOpen) {
          setShowForm(true);
          setTimeout(() => {
            if (agentFormRef.current) {
              agentFormRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 500);
        }
      }
    } catch (e) {
      console.error("Failed to load form state:", e);
    }
  };

  const clearFormData = () => {
    try {
      localStorage.removeItem("agent_form_data");
      localStorage.removeItem("agent_form_open");
    } catch (e) {
      console.error("Failed to clear form data:", e);
    }
  };

  useEffect(() => {
    loadFormData();
    loadFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  const setAgentNameWithSave = (value) => {
    setAgentName(value);
    setTimeout(saveFormData, 100);
  };

  const setPromptWithSave = (value) => {
    setPrompt(value);
    setTimeout(saveFormData, 100);
  };

  const setWelcomeMessageWithSave = (value) => {
    setWelcomeMessage(value);
    setTimeout(saveFormData, 100);
  };

  const setContactDetailsWithSave = (value) => {
    setContactDetails(value);
    setTimeout(saveFormData, 100);
  };

  const setKnowledgeBaseWithSave = (value) => {
    setKnowledgeBase(value);
    setTimeout(saveFormData, 100);
  };

  const setTrainingLinksWithSave = (value) => {
    setTrainingLinks(value);
    setTimeout(saveFormData, 100);
  };

  const setFaqWithSave = (value) => {
    setFaq(value);
    setTimeout(saveFormData, 100);
  };

  const setDomainWithSave = (value) => {
    setDomain(value);
    setTimeout(saveFormData, 100);
  };

  const setWidgetColorWithSave = (value) => {
    setWidgetColor(value);
    setTimeout(saveFormData, 100);
  };

  const setUseSupportStyleWithSave = (value) => {
    setUseSupportStyle(value);
    setTimeout(saveFormData, 100);
  };

  const setSupportNameWithSave = (value) => {
    setSupportName(value);
    setTimeout(saveFormData, 100);
  };

  const setAutoOpenEnabledWithSave = (value) => {
    setAutoOpenEnabled(value);
    setTimeout(saveFormData, 100);
  };

  const setAutoOpenDelayWithSave = (value) => {
    setAutoOpenDelay(value);
    setTimeout(saveFormData, 100);
  };

  const setAutoArchiveEnabledWithSave = (value) => {
    setAutoArchiveEnabled(value);
    setTimeout(saveFormData, 100);
  };

  const setAutoArchiveDelayWithSave = (value) => {
    setAutoArchiveDelay(value);
    setTimeout(saveFormData, 100);
  };

  const generateFaq = async () => {
    const effectiveDomain = (editingAgent ? editingAgent.domain : domain || "").trim();

    if (!effectiveDomain) {
      addNotification(t("Please enter a domain to generate FAQ"), "error");
      return;
    }

    setIsGeneratingFaq(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/generate-faq`,
        { domain: effectiveDomain },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (editingAgent) {
        setEditingAgent({ ...editingAgent, faq: response.data.faq });
      } else {
        setFaqWithSave(response.data.faq);
      }

      addNotification(
        t(
          "FAQ generated successfully! Please review and verify the information before saving."
        ),
        "info"
      );
    } catch (error) {
      console.error("Failed to generate FAQ:", error);
      addNotification(t("Failed to generate FAQ. Please try again."), "error");
    } finally {
      setIsGeneratingFaq(false);
    }
  };

  const generateTrainingLinks = async () => {
    const effectiveDomain = (editingAgent ? editingAgent.domain : domain || "").trim();

    if (!effectiveDomain) {
      addNotification(t("Please enter a domain to generate training links"), "error");
      return;
    }

    setIsGeneratingLinks(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/generate-training-links`,
        { domain: effectiveDomain },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (editingAgent) {
        setEditingAgent({
          ...editingAgent,
          training_links: response.data.links,
        });
      } else {
        setTrainingLinksWithSave(response.data.links);
      }

      addNotification(
        t(
          "Training links generated successfully! Please review and verify the links before saving."
        ),
        "info"
      );
    } catch (error) {
      console.error("Failed to generate training links:", error);
      addNotification(
        t("Failed to generate training links. Please try again."),
        "error"
      );
    } finally {
      setIsGeneratingLinks(false);
    }
  };

  const addNotification = (notificationMessage, type = "success") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message: notificationMessage, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const uploadWidgetIcon = async (agentId, file) => {
    const form = new FormData();
    form.append("file", file);
    const resp = await axios.post(`${API_URL}/agents/${agentId}/widget-icon`, form, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return resp.data;
  };

  const deleteWidgetIcon = async (agentId, iconUrl) => {
    const resp = await axios.delete(`${API_URL}/agents/${agentId}/widget-icon`, {
      params: { icon_url: iconUrl },
      headers: { Authorization: `Bearer ${token}` },
    });
    return resp.data;
  };

  const setActiveWidgetIcon = async (agentId, iconUrl) => {
    await axios.patch(`${API_URL}/agents/${agentId}/widget-icon`, null, {
      params: { icon_url: iconUrl },
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
  }, [token, navigate]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgents(response.data);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      if (error.response?.status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/?login=1");
      } else {
        setMessage(t("Failed to fetch agents"));
      }
    }
  };

  const fetchUserLimits = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/limits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.ok) {
        setUserLimits(response.data.limits);
      }
    } catch (error) {
      console.error("Failed to fetch user limits:", error);
    }
  };

  useEffect(() => {
    if (!userLimits || agents.length === 0) return;
    const maxActive = userLimits.agents?.max_active || 1;
    const activeCount = agents.filter((a) => a.is_active && !a.blocked_by_admin).length;

    let suppressedForCurrentRole = false;
    try {
      const roleName = (userLimits?.role_name || userLimits?.role || "").toString();
      const maxActiveAgents =
        userLimits?.agents?.max_active === -1
          ? "inf"
          : String(userLimits?.agents?.max_active || 1);
      const signature = `${roleName}:${maxActiveAgents}`;
      const stored = localStorage.getItem("agents_activation_suppressed_for");
      suppressedForCurrentRole = stored === signature;
    } catch (e) {}

    if (
      maxActive !== -1 &&
      activeCount > maxActive &&
      !suppressedForCurrentRole
    ) {
      const preselected = agents
        .filter((a) => a.is_active && !a.blocked_by_admin)
        .slice(0, maxActive)
        .map((a) => a.id);
      setActiveSelection(preselected);
      setShowActivationModal(true);
    } else {
      if (showActivationModal) setShowActivationModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLimits, agents]);

  const handleAgentSelection = async (selectedAgentId) => {
    if (!agentToActivate) return;

    try {
      const activeAgents = agents
        .filter(
          (a) =>
            a.is_active &&
            !a.blocked_by_admin &&
            a.id !== selectedAgentId
        )
        .map((a) => a.id);
      const newActiveAgents = [...activeAgents, agentToActivate];

      await axios.post(
        `${API_URL}/user/agents/activation`,
        { active_agent_ids: newActiveAgents },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAgents((prev) =>
        prev.map((a) => {
          if (a.id === selectedAgentId) return { ...a, is_active: false };
          if (a.id === agentToActivate) return { ...a, is_active: true };
          return a;
        })
      );

      const deactivatedAgent = agents.find((a) => a.id === selectedAgentId);
      const activatedAgent = agents.find((a) => a.id === agentToActivate);

      addNotification(
        t('Agent "{activated}" activated. Agent "{deactivated}" deactivated.', {
          activated: activatedAgent?.name ?? "",
          deactivated: deactivatedAgent?.name ?? "",
        }),
        "success"
      );

      setShowAgentSelectionModal(false);
      setAgentToActivate(null);
    } catch (e) {
      console.error("Failed to switch agents:", e);
      addNotification(t("Failed to switch agents"), "error");
    }
  };

  const toggleAgentActive = async (agentId) => {
    const agent = agents.find((a) => a.id === agentId);
    if (!agent) return;

    const isAdminBanned = agent.blocked_by_admin === true;
    if (isAdminBanned) {
      addNotification(
        t("This agent is suspended by administrator and cannot be activated."),
        "error"
      );
      return;
    }

    const newStatus = !agent.is_active;
    const maxActive = userLimits?.agents?.max_active || 1;
    const currentActive = agents.filter((a) => a.is_active && !a.blocked_by_admin).length;

    if (newStatus && maxActive !== -1 && currentActive >= maxActive) {
      setAgentToActivate(agentId);
      setShowAgentSelectionModal(true);
      return;
    }

    try {
      await axios.post(
        `${API_URL}/user/agents/activation`,
        {
          active_agent_ids: newStatus
            ? [
                ...agents
                  .filter((a) => a.is_active && !a.blocked_by_admin)
                  .map((a) => a.id),
                agentId,
              ]
            : agents
                .filter((a) => a.is_active && !a.blocked_by_admin && a.id !== agentId)
                .map((a) => a.id),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAgents((prev) =>
        prev.map((a) => (a.id === agentId ? { ...a, is_active: newStatus } : a))
      );

      addNotification(
        t("Agent {status} successfully", {
          status: newStatus ? t("activated") : t("deactivated"),
        }),
        "success"
      );
    } catch (e) {
      console.error("Failed to toggle agent status:", e);
      addNotification(t("Failed to change agent status"), "error");
    }
  };

  const toggleAgentSelection = (agentId) => {
    const maxActive = userLimits?.agents?.max_active || 1;
    const agent = agents.find((a) => a.id === agentId);
    const isAdminBanned = agent && agent.blocked_by_admin === true;
    if (isAdminBanned) return;

    const isSelected = activeSelection.includes(agentId);
    if (isSelected) {
      setActiveSelection(activeSelection.filter((id) => id !== agentId));
    } else {
      if (activeSelection.length >= maxActive && maxActive !== -1) return;
      setActiveSelection([...activeSelection, agentId]);
    }
  };

  const saveActiveAgents = async () => {
    try {
      const filteredActive = activeSelection.filter((id) => {
        const a = agents.find((x) => x.id === id);
        return !(a && a.blocked_by_admin === true);
      });

      await axios.post(
        `${API_URL}/user/agents/activation`,
        { active_agent_ids: filteredActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const selected = new Set(filteredActive);
      setAgents((prev) =>
        prev.map((a) => {
          const isAdminBanned = a.blocked_by_admin === true;
          return { ...a, is_active: isAdminBanned ? false : selected.has(a.id) };
        })
      );

      const deactivated = agents
        .filter((a) => {
          const isAdminBanned = a.blocked_by_admin === true;
          return !selected.has(a.id) && !isAdminBanned;
        })
        .map((a) => a.id);
      try {
        localStorage.setItem("user_deactivated_agents", JSON.stringify(deactivated));
      } catch (e) {}
      fetchUserLimits();
      setMessage(t("Active agents updated"));
      setShowActivationModal(false);
      try {
        const roleName = (userLimits?.role_name || userLimits?.role || "").toString();
        const maxActiveAgents =
          userLimits?.agents?.max_active === -1
            ? "inf"
            : String(userLimits?.agents?.max_active || 1);
        const signature = `${roleName}:${maxActiveAgents}`;
        localStorage.setItem("agents_activation_suppressed_for", signature);
      } catch (e) {}
    } catch (e) {
      console.error("Failed to set active agents:", e);
      setMessage(t("Failed to set active agents"));
    }
  };

  const handleAddAgent = async () => {
    if (!agentName.trim()) {
      addNotification(t("Agent Name is required"), "error");
      return;
    }
    if (!prompt) {
      addNotification(t("Prompt is required"), "error");
      return;
    }
    if (!domain.trim()) {
      addNotification(t("Domain is required"), "error");
      return;
    }

    const maxActive = userLimits?.agents?.max_active || 1;
    const currentActive = agents.filter((a) => a.is_active && !a.blocked_by_admin).length;
    const willBeInactive = maxActive !== -1 && currentActive >= maxActive;

    try {
      const response = await axios.post(
        `${API_URL}/agents`,
        {
          name: agentName,
          prompt,
          welcome_message: welcomeMessage,
          contact_details: contactDetails,
          knowledge_base: knowledgeBase,
          training_links: trainingLinks,
          faq: faq,
          domain,
          widget_color: widgetColor,
          use_support_style: useSupportStyle,
          support_name: supportName,
          auto_open_enabled: autoOpenEnabled,
          auto_open_delay: autoOpenDelay,
          auto_archive_enabled: autoArchiveEnabled,
          auto_archive_delay: autoArchiveDelay,
          sound_enabled: soundEnabled,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.first_agent_created_event) {
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "first_agent_created",
            agent_id: response.data.agent_id,
          });
        } catch (e) {
          console.error("Failed to push first_agent_created event:", e);
        }
      }

      // Upload icon (optional) after agent is created
      const createdAgentId = response?.data?.agent_id;
      if (createdAgentId && createIconFile) {
        setIsUploadingCreateIcon(true);
        setCreateIconUploadError("");
        try {
          await uploadWidgetIcon(createdAgentId, createIconFile);
        } catch (e) {
          console.error("Failed to upload widget icon:", e);
          setCreateIconUploadError(t("Failed to upload icon"));
        } finally {
          setIsUploadingCreateIcon(false);
          setCreateIconPreview(null);
          setCreateIconFile(null);
        }
      }

      setShowForm(false);

      if (willBeInactive) {
        addNotification(
          t(
            'Agent "{name}" created successfully! You have reached the maximum number of active agents. This agent is inactive.',
            { name: agentName }
          ),
          "info"
        );
      } else {
        addNotification(
          t('Agent "{name}" added! Agent ID: {id}', {
            name: agentName,
            id: response.data.agent_id,
          }),
          "success"
        );
      }
      setAgentName("");
      setPrompt("");
      setWelcomeMessage("");
      setContactDetails("");
      setKnowledgeBase("");
      setTrainingLinks("");
      setFaq("");
      setDomain("");
      setWidgetColor("#667eea");
      setUseSupportStyle(false);
      setSupportName("Support");
      setAutoOpenEnabled(false);
      setAutoOpenDelay(0);
      setAutoArchiveEnabled(false);
      setAutoArchiveDelay(0);
      clearFormData();
      await fetchUserLimits();
      await fetchAgents();
    } catch (error) {
      console.error("Failed to add agent:", error);
      if (error.response?.status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/?login=1");
      } else if (error.response?.status === 403) {
        addNotification(t("Agent limit exceeded"), "error");
      } else {
        const detail = error.response?.data?.detail;
        setMessage(typeof detail === "string" && detail.trim() ? detail : t("Failed to add agent"));
      }
    }
  };

  const handleEditAgent = async () => {
    if (!editingAgent.domain) {
      setMessage(t("Domain is required"));
      return;
    }
    try {
      await axios.put(
        `${API_URL}/agents/${editingAgent.id}`,
        {
          name: editingAgent.name,
          prompt: editingAgent.prompt,
          welcome_message: editingAgent.welcome_message,
          contact_details: editingAgent.contact_details,
          knowledge_base: editingAgent.knowledge_base,
          training_links: editingAgent.training_links,
          faq: editingAgent.faq,
          domain: editingAgent.domain,
          widget_color: editingAgent.widget_color || "#667eea",
          use_support_style: editingAgent.use_support_style,
          support_name: editingAgent.support_name,
          auto_open_enabled: editingAgent.auto_open_enabled,
          auto_open_delay: editingAgent.auto_open_delay,
          auto_archive_enabled: editingAgent.auto_archive_enabled,
          auto_archive_delay: editingAgent.auto_archive_delay,
          sound_enabled:
            typeof editingAgent.sound_enabled === "boolean"
              ? editingAgent.sound_enabled
              : true,
          icon_urls: getAgentIconUrls(editingAgent),
          widget_icon_url: editingAgent.widget_icon_url || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingAgent(null);
      addNotification(t('Agent "{name}" updated!', { name: editingAgent.name }), "success");
      fetchAgents();
    } catch (error) {
      console.error("Failed to update agent:", error);
      if (error.response?.status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/?login=1");
      } else {
        setMessage(t("Failed to update agent"));
      }
    }
  };

  const generateScript = async (agentId, isPreview = false) => {
    try {
      const response = await axios.get(`${API_URL}/api/get-widget-script/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let script = response.data;
      if (isPreview) {
        script = script.replace("preview: false", "preview: true");
      }
      return script;
    } catch (error) {
      console.error("Failed to generate script:", error);
      if (error.response?.status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/");
      } else {
        setMessage(t("Failed to generate script"));
      }
      return null;
    }
  };

  const handleGenerateScript = async (agentId) => {
    const script = await generateScript(agentId, false);
    if (script) {
      setGeneratedScript(script);
      const newScriptGenerated = { ...scriptGenerated, [agentId]: script };
      setScriptGenerated(newScriptGenerated);
      try {
        localStorage.setItem("generated_scripts", JSON.stringify(newScriptGenerated));
      } catch (e) {
        console.error("Failed to save script to localStorage:", e);
      }
      setScriptAgentId(agentId);
      navigator.clipboard.writeText(script);
      addNotification(t("Widget script generated and copied to clipboard!"), "success");
    }
  };

  const handleViewScript = (agentId) => {
    const script = scriptGenerated[agentId];
    if (script) {
      if (scriptAgentId === agentId && generatedScript) {
        setGeneratedScript("");
        setScriptAgentId(null);
      } else {
        setGeneratedScript(script);
        setScriptAgentId(agentId);
      }
    }
  };

  const handleShareScriptEmail = async (recipientEmail, scriptText) => {
    if (!token) throw new Error("Not authenticated");
    await axios.post(
      `${API_URL}/share-widget`,
      { recipient_email: recipientEmail, script: scriptText },
      {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      }
    );
    addNotification(
      t(
        "Email with widget installation instructions has been sent. Please ask the recipient to check the spam folder."
      ),
      "success"
    );
  };

  const startEditing = (agent) => {
    setEditingAgent({
      id: agent.id,
      name: agent.name,
      prompt: agent.prompt || "",
      welcome_message: agent.welcome_message || "",
      contact_details: agent.contact_details || "",
      knowledge_base: agent.knowledge_base || "",
      training_links: agent.training_links || "",
      faq: agent.faq || "",
      domain: agent.domain,
      widget_color: agent.widget_color || "#667eea",
      widget_icon_url: agent.widget_icon_url || null,
      icon_urls: getAgentIconUrls(agent),
      use_support_style: agent.use_support_style || false,
      support_name: agent.support_name || "Support",
      auto_open_enabled: agent.auto_open_enabled || false,
      auto_open_delay: agent.auto_open_delay || 0,
      auto_archive_enabled: agent.auto_archive_enabled || false,
      auto_archive_delay: agent.auto_archive_delay || 0,
      sound_enabled: typeof agent.sound_enabled === "boolean" ? agent.sound_enabled : true,
    });
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm(t("Are you sure you want to delete this agent?"))) return;
    try {
      await axios.delete(`${API_URL}/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      addNotification(t("Agent deleted successfully"), "success");

      const newScriptGenerated = { ...scriptGenerated };
      delete newScriptGenerated[agentId];
      setScriptGenerated(newScriptGenerated);
      try {
        localStorage.setItem("generated_scripts", JSON.stringify(newScriptGenerated));
      } catch (e) {
        console.error("Failed to update localStorage:", e);
      }

      await fetchUserLimits();
      await fetchAgents();
    } catch (error) {
      console.error("Failed to delete agent:", error);
      if (error.response?.status === 401) {
        setToken(null);
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate("/?login=1");
      } else {
        setMessage(t("Failed to delete agent"));
      }
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchUserLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const agentsAdminWsRef = useRef(null);
  const agentsAdminClientIdRef = useRef(
    `admin_${Math.random().toString(36).slice(2)}`
  );
  useEffect(() => {
    if (!agents || agents.length === 0) return;
    if (agentsAdminWsRef.current) {
      try {
        agentsAdminWsRef.current.close();
      } catch (e) {}
      agentsAdminWsRef.current = null;
    }
    const anyAgentId = agents[0]?.id || 0;
    const url = `${WS_URL}/ws/${anyAgentId}/${agentsAdminClientIdRef.current}:admin`;
    const sock = new WebSocket(url);
    sock.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "agent_status") {
          setAgents((prev) =>
            prev.map((a) =>
              a.id === data.agent_id ? { ...a, is_active: !!data.is_active } : a
            )
          );
          if (data.is_active === false) {
            setMessage(t("Agent was suspended by administrator"));
          }
        } else if (
          data.type === "agent_deleted" ||
          data.type === "agent_added" ||
          data.type === "limits_updated"
        ) {
          fetchUserLimits();
          fetchAgents();
        }
      } catch (_) {}
    };
    agentsAdminWsRef.current = sock;
    return () => {
      try {
        sock.close();
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents?.length]);

  return (
    <div className="agents">
      <ShareWidgetModal
        isOpen={isShareModalOpen && !!generatedScript}
        onClose={() => setIsShareModalOpen(false)}
        script={generatedScript}
        t={t}
        onSend={handleShareScriptEmail}
      />
      <CheckWidgetModal
        isOpen={!!checkWidgetAgent}
        onClose={() => setCheckWidgetAgent(null)}
        agentId={checkWidgetAgent?.id}
        agentDomain={checkWidgetAgent?.domain}
        token={token}
        t={t}
        apiUrl={API_URL}
      />

      <div className="notifications-container">
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className={`notification notification-${notif.type}`}
          >
            {notif.message}
            <button
              className="notification-close"
              onClick={() =>
                setNotifications((prev) => prev.filter((n) => n.id !== notif.id))
              }
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <button
          className={showAddAgentHighlight ? "add-agent-btn add-agent-btn--highlight" : "add-agent-btn"}
          onClick={() => {
            if (showAddAgentHighlight) {
              try {
                localStorage.removeItem("welcome_tour_highlight_add_agent");
              } catch (e) {}
              setShowAddAgentHighlight(false);
            }
            setShowForm(true);
            setTimeout(() => {
              if (agentFormRef.current) {
                agentFormRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }, 100);
          }}
        >
          {t("Add Agent")}
        </button>
        {message && <span style={{ color: "#6c757d", fontSize: 13 }}>{message}</span>}
      </div>

      <ul style={{ marginTop: 14 }}>
        {agents.map((agent) => (
          <li
            key={agent.id}
            className={!agent.is_active ? "agent-blocked" : ""}
          >
            <div className="agent-info">
              <div className="agent-details">
                <span className="agent-name">
                  <TruncatedText text={agent.name} maxLength={20} /> (
                  {agent.domain})
                </span>

                {!agent.is_active && (
                  <div className="blocked-notice">
                    <span className="blocked-icon">⚠️</span>
                    <span className="blocked-text">
                      {agent.blocked_by_admin === true ? (
                        <>
                          {t(
                            "This widget has been suspended by administrator due to policy violations."
                          )}
                          <br />
                          {t("Please contact support at")}{" "}
                          <a href="mailto:support@supchats.com">
                            support@supchats.com
                          </a>{" "}
                          {t("or")} <Link to="/contact">{t("open Contact Us")}</Link>
                          .
                        </>
                      ) : (
                        t(
                          "This widget is currently inactive. Click the play button to activate it."
                        )
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="agent-actions">
                <button
                  onClick={() => toggleAgentActive(agent.id)}
                  className={`toggle-active-btn ${
                    agent.blocked_by_admin === true
                      ? "banned"
                      : agent.is_active
                        ? "active"
                        : "inactive"
                  }`}
                  title={
                    agent.blocked_by_admin === true
                      ? t("Agent suspended by administrator")
                      : agent.is_active
                        ? t("Deactivate agent")
                        : t("Activate agent")
                  }
                  disabled={agent.blocked_by_admin === true}
                >
                  {agent.blocked_by_admin === true ? "🚫" : agent.is_active ? "⏸" : "▶"}
                </button>

                {scriptGenerated[agent.id] ? (
                  <button onClick={() => handleViewScript(agent.id)}>
                    {t("View Script")}
                  </button>
                ) : (
                  <button onClick={() => handleGenerateScript(agent.id)}>
                    {t("Generate Script")}
                  </button>
                )}

                <button onClick={() => startEditing(agent)}>{t("Edit")}</button>
                <button
                  onClick={() => window.open(`${window.location.origin}/test-widget/${agent.id}`, "_blank", "noopener,noreferrer")}
                  title={t("Test widget in a new tab")}
                >
                  {t("Test")}
                </button>
                <button
                  onClick={() => handleDeleteAgent(agent.id)}
                  className="delete-button"
                >
                  {t("Delete")}
                </button>
              </div>
            </div>

            {editingAgent && editingAgent.id === agent.id && (
              <AgentForm
                mode="edit"
                value={editingAgent}
                onPatch={patchEditingAgent}
                iconUrls={getAgentIconUrls(editingAgent)}
                toAbsoluteIconUrl={toAbsoluteIconUrl}
                iconUploading={isUploadingEditIcon}
                iconError={editIconUploadError}
                iconPreviewUrl={editIconPreviewUrl}
                onPickIconFile={async (file) => {
                  if (!editingAgent?.id) return;
                  if (editIconPreviewUrlRef.current) URL.revokeObjectURL(editIconPreviewUrlRef.current);
                  const previewUrl = URL.createObjectURL(file);
                  editIconPreviewUrlRef.current = previewUrl;
                  setEditIconPreviewUrl(previewUrl);
                  setEditIconUploadError("");
                  setIsUploadingEditIcon(true);
                  try {
                    const data = await uploadWidgetIcon(editingAgent.id, file);
                    if (data?.icon_urls) {
                      const nextUrls = Array.isArray(data.icon_urls) ? [...data.icon_urls] : [];
                      patchEditingAgent({
                        widget_icon_url: data.widget_icon_url || null,
                        icon_urls: nextUrls,
                      });
                      setAgents((prev) =>
                        prev.map((a) =>
                          a.id === editingAgent.id
                            ? { ...a, widget_icon_url: data.widget_icon_url || null, icon_urls: nextUrls }
                            : a
                        )
                      );
                    }
                  } catch (e) {
                    console.error("Failed to upload widget icon:", e);
                    setEditIconUploadError(
                      e.response?.data?.detail || t("Failed to upload icon")
                    );
                  } finally {
                    setIsUploadingEditIcon(false);
                    if (editIconPreviewUrlRef.current) {
                      URL.revokeObjectURL(editIconPreviewUrlRef.current);
                      editIconPreviewUrlRef.current = null;
                    }
                    setEditIconPreviewUrl("");
                  }
                }}
                onDeleteIcon={async (iconUrl) => {
                  if (!editingAgent?.id) return;
                  try {
                    const data = await deleteWidgetIcon(editingAgent.id, iconUrl);
                    const nextUrls = Array.isArray(data?.icon_urls) ? [...data.icon_urls] : [];
                    patchEditingAgent({
                      widget_icon_url: data?.widget_icon_url ?? null,
                      icon_urls: nextUrls,
                    });
                    setAgents((prev) =>
                      prev.map((a) =>
                        a.id === editingAgent.id
                          ? { ...a, widget_icon_url: data?.widget_icon_url ?? null, icon_urls: nextUrls }
                          : a
                      )
                    );
                  } catch (err) {
                    console.error("Failed to delete icon:", err);
                  }
                }}
                onSubmit={handleEditAgent}
                onCancel={() => {
                  if (editIconPreviewUrlRef.current) {
                    URL.revokeObjectURL(editIconPreviewUrlRef.current);
                    editIconPreviewUrlRef.current = null;
                  }
                  setEditIconPreviewUrl("");
                  setEditingAgent(null);
                }}
                t={t}
                isGeneratingFaq={isGeneratingFaq}
                isGeneratingLinks={isGeneratingLinks}
                onGenerateFaq={generateFaq}
                onGenerateTrainingLinks={generateTrainingLinks}
                className="agent-form agent-form--inline-full"
              />
            )}

            {scriptAgentId === agent.id && generatedScript && (
              <div className="agent-form agent-form--inline-full">
                <div className="field-title">
                  <strong>{t("Generated Script")}</strong>
                </div>
                <textarea readOnly value={generatedScript} rows="10" />
                <div className="agent-form-buttons">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedScript);
                      addNotification(t("Script copied to clipboard!"), "success");
                    }}
                  >
                    {t("Copy")}
                  </button>
                  <button type="button" onClick={() => setIsShareModalOpen(true)}>
                    {t("Share")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCheckWidgetAgent(agent)}
                  >
                    {t("Check widget installation")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedScript("");
                      setScriptAgentId(null);
                    }}
                  >
                    {t("Close")}
                  </button>
                </div>
                <div style={{ marginTop: 8 }}>
                  <a
                    href="/documentation#installing-widget"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Need help? Read how to place the script on your site →")}
                  </a>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {showForm && !editingAgent && (
        <div ref={agentFormRef}>
          <AgentForm
            mode="create"
            value={createFormValue}
            onPatch={patchCreateForm}
            iconUrls={[]}
            iconPreviewUrl={createIconPreviewUrl}
            toAbsoluteIconUrl={toAbsoluteIconUrl}
            iconUploading={isUploadingCreateIcon}
            iconError={createIconUploadError}
            onPickIconFile={(file) => {
              setCreateIconUploadError("");
              setCreateIconPreview(file);
              setCreateIconFile(file);
            }}
            onClearIconPreview={() => {
              setCreateIconPreview(null);
              setCreateIconFile(null);
              if (createIconPreviewUrlRef.current) {
                URL.revokeObjectURL(createIconPreviewUrlRef.current);
                createIconPreviewUrlRef.current = null;
              }
            }}
            onSubmit={handleAddAgent}
            onCancel={() => {
              setShowForm(false);
              clearFormData();
              setCreateIconPreview(null);
              setCreateIconFile(null);
              setCreateIconUploadError("");
            }}
            t={t}
            isGeneratingFaq={isGeneratingFaq}
            isGeneratingLinks={isGeneratingLinks}
            onGenerateFaq={generateFaq}
            onGenerateTrainingLinks={generateTrainingLinks}
            className="agent-form"
          />
        </div>
      )}

      {showActivationModal && (
        <div className="modal-overlay" onClick={() => setShowActivationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3>{t("Select Active Agents")}</h3>
              <button className="modal-close" onClick={() => setShowActivationModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, maxHeight: 300, overflowY: "auto" }}>
                {agents.map((a) => (
                  <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, border: "1px solid #eee", borderRadius: 8 }}>
                    <input
                      type="checkbox"
                      checked={activeSelection.includes(a.id)}
                      onChange={() => toggleAgentSelection(a.id)}
                      disabled={a.blocked_by_admin === true}
                    />
                    <span style={{ fontWeight: 600 }}>
                      <TruncatedText text={a.name} maxLength={25} />
                    </span>
                    <span style={{ color: "#888" }}>({a.domain})</span>
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                <button onClick={() => setShowActivationModal(false)}>{t("Cancel")}</button>
                <button onClick={saveActiveAgents} className="btn btn-info">
                  {t("Save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAgentSelectionModal && (
        <div className="modal-overlay" onClick={() => setShowAgentSelectionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3>🔄 {t("Switch Active Agent")}</h3>
              <button className="modal-close" onClick={() => setShowAgentSelectionModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, maxHeight: 350, overflowY: "auto" }}>
                {agents
                  .filter((a) => a.is_active && !a.blocked_by_admin)
                  .map((a) => (
                    <button
                      key={a.id}
                      onClick={() => handleAgentSelection(a.id)}
                      style={{
                        padding: 14,
                        border: "2px solid #e9ecef",
                        borderRadius: 12,
                        background: "#fff",
                        cursor: "pointer",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        <TruncatedText text={a.name} maxLength={30} />
                      </div>
                      <div style={{ color: "#6c757d", fontSize: "0.9em" }}>{a.domain}</div>
                    </button>
                  ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
                <button
                  onClick={() => setShowAgentSelectionModal(false)}
                  style={{
                    padding: "10px 20px",
                    border: "1px solid #6c757d",
                    background: "#fff",
                    color: "#6c757d",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  {t("Cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

